import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, TextInput, StyleSheet, Pressable, TouchableOpacity, Modal, ActivityIndicator, FlatList, ScrollView, Icon, Icon2 } from '../lib/react-native-compat';
import { loadUserData, deleteDayZero, deleteUserData, fNum, pNum, updateUserDataArray, loadJobHardDollar, loadBudgetHardDollar, loadGLAccount, loadBudgetExists, loadVersionTeamJob } from '../db/company';
import { dropBlankRows, versionBuildBlock, MAX_UPLOAD_ROWS } from '../helpers/dayZeroUpload';
import { selectedJobAtom, jobUserDataAtom, selectedVersionAtom, isLiveVersionAtom, jobPhaseCodesAtom, selectedVersionLockedAtom, phasedProjectAtom, userNameAtom, userIdAtom, userEmailCheckAtom, versionUnlocksAtom } from '../store/company';
import { activeVersionUnlock } from '../db/versionUnlock';
import { useAtom } from 'jotai';
import XLSX from 'xlsx';
import RevenueBudget from './revenueBudget';
import DayZeroHistory from './dayZeroHistory';
import { isVoltaGridJob } from '../helpers/voltaGrid';
import { dayZeroBillable, dayZeroRevenueLines, revenueBudgetSaves, DAY_ZERO_REVENUE_HEADERS } from '../helpers/revenueBudget';
import { Doughnut } from 'react-chartjs-2';
import { SERIES, SERIES_OTHER } from '../styles/chartColors';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

// updateUserDataArray pastes each field into its SQL as text, so an apostrophe,
// a "$" or an "@token" from the uploaded sheet breaks the whole batch. Keep
// only characters that are inert there.
const sqlSafeText = (v) => (v === null || v === undefined ? '' : String(v).replace(/[^a-zA-Z0-9.,:;#_%+\/ \-\^~&()*=]/g, '').trim());

const DayZeroBudget = () => {
    const [selectedJob, setSelectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion, setSelectedVersion] = useAtom(selectedVersionAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [userName] = useAtom(userNameAtom);
    const [userId] = useAtom(userIdAtom);
    const [userEmailCheck] = useAtom(userEmailCheckAtom);
    const canSeeBudgetOverview = true;
    const [file, setFile] = useState(null);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [phaseCodes, setPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [lockedVersion, setLockedVersion] = useAtom(selectedVersionLockedAtom)
    const [versionUnlocks] = useAtom(versionUnlocksAtom);
    const [rows, setRows] = useState({});
    const fileInput = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedHardDollarJob, setSelectedHardDollarJob] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [costTypeDropdown, setCostTypeDropdown] = useState({});
    const [grouped, setGrouped] = useState({});
    const [noCostType, setNoCostType] = useState([]);
    const [data, setData] = useState([]);
    const [nextPageModal, setNextPageModal] = useState(false);
    const [phased, setPhased] = useAtom(phasedProjectAtom);
    const [totalsByType, setTotalsByType] = useState({});
    const [sectionTotals, setSectionTotals] = useState({});
    const [waitingQuery, setWaitingQuery] = useState(false);
    const [unassignedTotal, setUnassignedTotal] = useState(0);
    const [grandTotal, setGrandTotal] = useState(0);
    const [glValues, setGlValues] = useState({});
    const [notFound, setNotFound] = useState([]);
    const [failedUploads, setFailedUploads] = useState([]);
    const [bulkUploadStatus, setBulkUploadStatus] = useState(null);
    const [bulkUploadProgress, setBulkUploadProgress] = useState({ current: 0, total: 0 });
    const [isBulkUploading, setIsBulkUploading] = useState(false);
    const [budgetExists, setBudgetExists] = useState(false);
    // VoltaGrid jobs carry a Revenue Budget beside the cost budget.
    const isVg = isVoltaGridJob(selectedJob);
    const [budgetView, setBudgetView] = useState('cost');
    // Import lines JDE has no combination for aren't saved; they stay on the
    // grid (red) across the reload that follows an upload.
    const unsavedRows = useRef([]);
    const [revenueStatus, setRevenueStatus] = useState(null);
    // An upload in flight. A 700-line upload takes about half a minute with
    // nothing to see; on 10/15/2026 (12426039) the file was picked again and
    // the page reloaded mid-save, and the version was left holding four
    // budgets. A second upload waits for the first, and leaving the page asks.
    const uploading = useRef(false);
    const [uploadBusy, setUploadBusy] = useState(false);

    // List of jobs for bulk upload
    const bulkUploadJobs = [
        '12025034', '15525003', '54025083', '11625219', '11625220',
        '11625221', '11625222', '11625226', '12025096', '11625227', '11625228',
        '11625229', '11625230', '11625231', '16125007', '11625232', '11625233',
        '54025085', '54025086', '54025087', '54025088', '54025089', '11625234',
        '11625235', '11625236', '12025097', '54025090', '11625237', '11625238',
        '54025091', '12425048', '50225025', '54025092', '50225027', '11625239',
        '11625240'
    ];

    useEffect(() => {
        if (jobUserData && jobUserData.length > 0) {
            const userData = {}
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Day Zero Budget') {
                    userData[d?.Forecast_Row_ID] = d
                }
            })
            unsavedRows.current.forEach((r, i) => { userData[`unsaved-${i}`] = r })
            console.log('userData', userData, jobUserData)
            setRows(userData)
        }
    }, [jobUserData])

    useEffect(() => {
        if (selectedJob && selectedVersion) {
            unsavedRows.current = [];
            setRevenueStatus(null);
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then((data) => {
                setJobUserData(data?.data);
            })
        }
    }, [selectedJob, selectedVersion])

    useEffect(() => {
        if (selectedJob) {
            loadGLAccount({ ids: [selectedJob] }).then((data) => {
                console.log('gl values', data?.data)
                setGlValues(data?.data)
            })
        }
    }, [selectedJob])

    useEffect(() => {
        if (selectedJob) {
            // loadBudgetExists({ id: selectedJob }).then((data) => {
            //     console.log('budget exists check', data?.data)
            //     const exists = data?.data && data.data.length > 0
            //     setBudgetExists(exists)
            // })
        }
    }, [selectedJob])

    // Billable cost per grid row, off the version's Revenue Budget (the import's
    // Billable Cost column). No budget, no column.
    const rowList = useMemo(() => Object.values(rows || {}), [rows]);
    const billable = useMemo(() => dayZeroBillable({ dayZeroRows: rowList, userData: jobUserData, version: selectedVersion }), [rowList, jobUserData, selectedVersion]);

    // Phase code -> discipline mapping, sourced from the Productivity Tracker tab
    // (same source used by cologUpload / allThirdParty so disciplines stay consistent).
    const disciplineMap = useMemo(() => {
        const m = {};
        (jobUserData || []).forEach((d) => {
            if (d?.Forecast_Tab === 'Productivity Tracker' && d?.Forecast_Field_Name === 'discipline') {
                m[d?.Forecast_Row_ID] = d?.Forecast_Value;
            }
        });
        return m;
    }, [jobUserData])

    const handleBulkUploadBudgets = async () => {
        setIsBulkUploading(true);
        setBulkUploadProgress({ current: 0, total: bulkUploadJobs.length });
        setBulkUploadStatus('Starting bulk upload...');

        console.log('Starting bulk budget upload for jobs:', bulkUploadJobs);

        const results = {
            successful: [],
            failed: [],
            total: bulkUploadJobs.length
        };

        for (let i = 0; i < bulkUploadJobs.length; i++) {
            const jobNumber = bulkUploadJobs[i];
            setBulkUploadProgress({ current: i + 1, total: bulkUploadJobs.length });
            setBulkUploadStatus(`Processing job ${jobNumber} (${i + 1}/${bulkUploadJobs.length})`);

            try {
                console.log(`Loading user data for job: ${jobNumber}`);
                
                // Load user data for this specific job
                const userData = await loadUserData({id: jobNumber, forecastGuid: selectedVersion});
                
                if (!userData || !userData.data) {
                    console.log(`No user data found for job: ${jobNumber}`);
                    results.failed.push({ job: jobNumber, error: 'No user data found' });
                    continue;
                }

                // Filter for Day Zero Budget data
                const budgetData = userData.data.filter(d => d?.Forecast_Tab === 'Day Zero Budget');
                
                if (!budgetData || budgetData.length === 0) {
                    console.log(`No budget data found for job: ${jobNumber}`);
                    results.failed.push({ job: jobNumber, error: 'No budget data found' });
                    continue;
                }

                // Convert user data to the correct format for STR_ORCH_Fusion_UploadBudget
                const uploadRequests = budgetData
                    .filter(item => {
                        // Filter out blank/empty objects
                        const hasValidData = item && (
                            item['Cost_Type'] || item['Cost Type'] ||
                            item['Cost_Code'] || item['Cost Code'] ||
                            item['Value_Amount'] || item.Value_Amount ||
                            item['Hours'] || item.Hours ||
                            item['Quantity'] || item.Quantity
                        );
                        return hasValidData;
                    })
                    .map((item) => {
                        return {
                            inORCH_BusUnit: jobNumber,
                            inORCH_Object: pNum(item['Cost_Type'] || item['Cost Type']) ? pNum(item['Cost_Type'] || item['Cost Type']) : '      ', // default to spaces if Cost_Type is not provided
                            inORCH_Sub: pNum(item['Cost_Code'] || item['Cost Code']) ? pNum(item['Cost_Code'] || item['Cost Code']) : '', // default to empty if Cost_Code is not provided
                            inORCH_UserID: '154045', // update if dynamic
                            inORCH_BudgetAmt: (item['Cost_Type'] || item['Cost Type'])?.[0] == '4' ? -1 * pNum(item['Value_Amount'] || item.Value_Amount) : pNum(item['Value_Amount'] || item.Value_Amount),
                            inORCH_BudgetUnits: (item['Cost_Type'] || item['Cost Type']) ? pNum(item['Hours'] || item.Hours) : pNum(item['Quantity'] || item.Quantity)
                        };
                    });

                console.log(`Uploading`, uploadRequests, jobNumber);

                // Send each upload request for this job
                for (let j = 0; j < uploadRequests.length; j++) {
                    const uploadData = uploadRequests[j];
                    try {
                        // Add delay every 10 items
                        if (j % 10 === 0 && j > 0) {
                            await new Promise(resolve => setTimeout(resolve, 1000));
                        }

                        const response = await fetch('https://jdeorch.strikeusa.com:443/jderest/orchestrator/STR_ORCH_Fusion_UploadBudget', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': 'Basic MTQ4Njk1Oks2b1chKmVJZ216dDZWd2Y='
                            },
                            body: JSON.stringify(uploadData)
                        });

                        const responseData = await response.json();
                        
                        if (responseData?.jde__status !== "SUCCESS") {
                            console.error(`Failed to upload row ${j + 1} for job ${jobNumber}:`, responseData);
                        } else {
                            console.log(`Successfully uploaded row ${j + 1} for job ${jobNumber}`);
                        }

                        // Small delay between requests to avoid overwhelming the server
                        await new Promise(resolve => setTimeout(resolve, 50));
                    } catch (error) {
                        console.error(`Failed to upload row ${j + 1} for job ${jobNumber}:`, error);
                    }
                }

                results.successful.push(jobNumber);
                console.log(`Completed upload for job: ${jobNumber}`);

                // Delay between jobs to avoid overwhelming the server
                await new Promise(resolve => setTimeout(resolve, 200));

            } catch (error) {
                console.error(`Failed to process job ${jobNumber}:`, error);
                results.failed.push({ job: jobNumber, error: error.message });
            }
        }

        setBulkUploadStatus('Upload complete!');
        setIsBulkUploading(false);

        // Call the finish upload process
        try {
            setBulkUploadStatus('Finalizing upload process...');
            await finishUploadToJDE();
        } catch (error) {
            console.error('Failed to finalize upload process:', error);
        }

        // Show completion toast
        console.log('Toast:', 'Bulk Upload Complete', `Success: ${results.successful.length}, Failed: ${results.failed.length}`);

        console.log('Bulk upload results:', results);

        // Reset status after a delay
        setTimeout(() => {
            setBulkUploadStatus(null);
            setBulkUploadProgress({ current: 0, total: 0 });
        }, 3000);
    };

    const importBudgetHardDollar = async () => {
        try {
            setIsLoading(true);

            // Fetch list of jobs
            const jobList = await loadJobHardDollar();
            console.log('job list', jobList?.data)
            setJobs(jobList?.data);

            // // Check if the selected job exists in the list
            // const matchingJob = jobList && jobList.data ? jobList.data.find((job) => job.code === selectedJob) : null

            // if (matchingJob) {
            //     // Proceed directly to budget import if job is found
            //     Toast.show({
            //         type: 'success',
            //         text1: 'Job Found',
            //         text2: `Job ${selectedJobCode} exists. Importing budget...`,
            //     });

            //     const budgetResponse = await loadBudgetHardDollar(selectedJob);

            //     setIsLoading(false);
            //     Toast.show({
            //         type: 'success',
            //         text1: 'Import Successful',
            //         text2: 'Budget data imported successfully.',
            //     });

            //     console.log('Budget Data a:', budgetResponse); // Process the data
            // } else {
            // Show modal to allow user to select a job
            setFilteredJobs(jobList?.data);
            setIsModalVisible(true);
            setIsLoading(false);
            //}
        } catch (error) {
            setIsLoading(false);
            console.log('Toast:', 'Error', error.message || 'Failed to load job list.');
        }
    };

    const syncBulkUploadToJDE = async (foundCombinations) => {
        console.log('Starting bulk upload for current job:', selectedJob);
        
        try {
            // Convert foundCombinations to the correct format for STR_ORCH_Fusion_UploadBudget
            const uploadRequests = foundCombinations
                .filter(item => {
                    // Filter out blank/empty objects
                    const hasValidData = item && (
                        item['Cost_Type'] ||
                        item['Cost_Code'] ||
                        item['Value_Amount'] ||
                        item['Hours'] ||
                        item['Quantity']
                    );
                    return hasValidData;
                })
                .map((item) => {
                    return {
                        inORCH_BusUnit: selectedJob,
                        inORCH_Object: pNum(item['Cost_Type']) ? pNum(item['Cost_Type']) : '      ',
                        inORCH_Sub: pNum(item['Cost_Code']) ? pNum(item['Cost_Code']) : '',
                        inORCH_UserID: '154045',
                        inORCH_BudgetAmt: item['Cost_Type']?.[0] == '4' ? -1 * pNum(item['Value_Amount']) : pNum(item['Value_Amount']),
                        inORCH_BudgetUnits: item['Cost_Type'] ? pNum(item['Hours']) : pNum(item['Quantity'])
                    };
                });

            console.log(`Uploading ${uploadRequests.length} budget items for job ${selectedJob}`);

            // Send each upload request for this job
            for (let j = 0; j < uploadRequests.length; j++) {
                const uploadData = uploadRequests[j];
                try {
                    // Add delay every 10 items
                    if (j % 10 === 0 && j > 0) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }

                    const response = await fetch('https://jdeorch.strikeusa.com:443/jderest/orchestrator/STR_ORCH_Fusion_UploadBudget', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Basic MTQ4Njk1Oks2b1chKmVJZ216dDZWd2Y='
                        },
                        body: JSON.stringify(uploadData)
                    });

                    const responseData = await response.json();
                    
                    if (responseData?.jde__status !== "SUCCESS") {
                        console.error(`Failed to upload row ${j + 1} for job ${selectedJob}:`, responseData);
                        const failedData = {
                            jobId: selectedJob,
                            costType: uploadData.inORCH_Object,
                            phaseCode: uploadData.inORCH_Sub,
                            value: uploadData.inORCH_BudgetAmt,
                            message: JSON.stringify(responseData?.message)
                        };
                        setFailedUploads((prev) => [...prev, failedData]);
                    } else {
                        console.log(`Successfully uploaded row ${j + 1} for job ${selectedJob}`);
                    }

                    // Small delay between requests to avoid overwhelming the server
                    await new Promise(resolve => setTimeout(resolve, 50));
                } catch (error) {
                    console.error(`Failed to upload row ${j + 1} for job ${selectedJob}:`, error);
                    const failedData = {
                        jobId: selectedJob,
                        costType: uploadData?.inORCH_Object,
                        phaseCode: uploadData?.inORCH_Sub,
                        value: uploadData?.inORCH_BudgetAmt,
                        message: error.message
                    };
                    setFailedUploads((prev) => [...prev, failedData]);
                }
            }

            console.log(`Completed bulk upload for job: ${selectedJob}`);

            // Call the finish upload process
            try {
                console.log('Finalizing upload process...');
                await finishUploadToJDE();
                
                console.log('Toast:', 'Budget Upload Complete', `Successfully uploaded budget for job ${selectedJob}`);
            } catch (error) {
                console.error('Failed to finalize upload process:', error);
                console.log('Toast:', 'Upload Error', 'Budget uploaded but failed to finalize process');
            }

        } catch (error) {
            console.error(`Failed to process bulk upload for job ${selectedJob}:`, error);
            console.log('Toast:', 'Upload Failed', `Failed to upload budget for job ${selectedJob}: ${error.message}`);
        }
    };

    const uploadToJDE = async (rows) => {
        console.log('rows', rows)
        const combinedItems = Object.values(rows);
        const uploadPromises = [];
        
        for (const row of combinedItems) {
            try {
                if (combinedItems.indexOf(row) % 10 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }

                const uploadPromise = fetch('https://jdeorch.strikeusa.com:443/jderest/orchestrator/STR_ORCH_Fusion_UploadBudget', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Basic MTQ4Njk1Oks2b1chKmVJZ216dDZWd2Y='
                    },
                // await fetch('https://jdeorchtest.strikeusa.com/jderest/orchestrator/STR_ORCH_Fusion_UploadBudget', {
                //     method: 'POST',
                //     headers: {
                //         'Content-Type': 'application/json',
                //         'Authorization': 'Basic MTQ4Njk1Oks2b1chKmVJZ216dDZWd2Y=' // Change if needed
                //     },
                    body: JSON.stringify({
                        inORCH_BusUnit: selectedJob,
                        inORCH_Object: pNum(row['Cost_Type']) ? pNum(row['Cost_Type']) : '      ', // default to 51100 if Cost_Type is not provided
                        inORCH_Sub: pNum(row['Cost_Code']) ? pNum(row['Cost_Code']) : '', // default to 00000 if Cost_Code is not provided
                        inORCH_UserID: '154045', // update if dynamic
                        inORCH_BudgetAmt: row['Cost Type']?.[0] == '4' ? -1 * pNum(row.Value_Amount) : pNum(row.Value_Amount),
                        inORCH_BudgetUnits: row['Cost Type'] ? pNum(row.Hours) : pNum(row.Quantity)
                    })
                })
                    .then(response => response.json())
                    .then((data) => {
                        if (data?.jde__status !== "SUCCESS") {
                            const failedData = {
                                jobId: selectedJob,
                                costType: row['Cost Type'],
                                phaseCode: row['Cost Code'],
                                value: row.Amount,
                                message: JSON.stringify(data?.message)
                            };

                            setFailedUploads((prev) => [...prev, failedData]);
                            errorList.push({ error: data?.jde__status });
                        }
                    })
                    .catch((err) => {
                        const failedData = {
                            jobId: row.jobId,
                            costType: row.costType,
                            phaseCode: row.phaseCode,
                            value: row.value,
                            message: err.message
                        };
                        setFailedUploads((prev) => [...prev, failedData]);
                        errorList.push({ error: err });
                    });

                uploadPromises.push(uploadPromise);

            } catch (error) {
                console.error('Upload Error:', error);
                errorList.push({ error: error });
            }
        }

        // Wait for all uploads to complete, then call finishUploadToJDE
        try {
            await Promise.allSettled(uploadPromises);
            console.log('All uploads completed, calling finishUploadToJDE...');
            await finishUploadToJDE();
        } catch (error) {
            console.error('Error in upload process:', error);
        }

    };

    const finishUploadToJDE = async () => {
        try {
            console.log('Finishing upload to JDE...');
            
            // Get current date and calculate fiscal year and period
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-based
            const currentYear = currentDate.getFullYear();
            
            // Assuming fiscal year starts in October (month 10)
            // If current month is Oct-Dec, fiscal year is current year + 1
            // If current month is Jan-Sep, fiscal year is current year
            const fiscalYear = currentMonth >= 10 ? currentYear + 1 : currentYear;
            const fiscalYearShort = fiscalYear.toString().slice(-2); // Last 2 digits
            
            // Calculate period number (Oct=1, Nov=2, ..., Sep=12)
            const periodNumber = currentMonth >= 10 ? currentMonth - 9 : currentMonth + 3;
            
            console.log(`Dynamic fiscal year: ${fiscalYear} (${fiscalYearShort}), Period: ${periodNumber}`);
            
            const response = await fetch('https://jdeorch.strikeusa.com:443/jderest/orchestrator/STR_ORCH_Fusion_ProcessBudgetUpload', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic MTQ4Njk1Oks2b1chKmVJZ216dDZWd2Y='
                },
                body: JSON.stringify({
                    "inORCH_Cent": "20",
                    "inORCH_FY": fiscalYearShort,
                    "inORCH_BegPeriodNbr": periodNumber.toString(),
                    "inORCH_EndPeriodNbr": periodNumber.toString(),
                    "inORCH_BegFiscalYear": fiscalYear.toString(),
                    "inORCH_EndFiscalYear": fiscalYear.toString()
                })
            });

            const data = await response.json();
            console.log('Finish upload response:', data);

            if (data?.jde__status === "SUCCESS") {
                console.log('Toast:', 'Upload Completed', 'Budget upload process finished successfully.');
            } else {
                console.log('Toast:', 'Upload Error', data?.message || 'Failed to complete budget upload process.');
            }
        } catch (error) {
            console.error('Finish Upload Error:', error);
            console.log('Toast:', 'Upload Error', error.message || 'Failed to complete budget upload process.');
        }
    };

    const handleSearch = (text) => {
        setSearchText(text);
        if (text === '') {
            setFilteredJobs(jobs);
        } else {
            const filtered = jobs.filter(
                (job) =>
                    job.Code?.toLowerCase().includes(text?.toLowerCase()) ||
                    job.Description?.toLowerCase().includes(text?.toLowerCase())
            );
            setFilteredJobs(filtered);
        }
    };

    const handleContinue = async () => {
        setIsLoading(false);

        try {
            if (!nextPageModal) {
                setWaitingQuery(true);
                console.log('selectedHardDollarJob', selectedHardDollarJob)
                const budgetResponse = await loadBudgetHardDollar({ id: selectedHardDollarJob.Code });
                console.log('Budget Data b:', budgetResponse); // Process the data
                setData(budgetResponse.data);
                preprocessData(budgetResponse.data);
                setNextPageModal(true);
                setWaitingQuery(false);

                // setIsLoading(false);
                // Toast.show({
                //     type: 'success',
                //     text1: 'Import Successful',
                //     text2: 'Budget data imported successfully.',
                // });

                // console.log('Budget Data:', budgetResponse); // Process the data
            } else {
                setIsModalVisible(false);

            }
        } catch (error) {
            setIsLoading(false);
            console.log('Toast:', 'Error', error.message || 'Failed to import budget.');
        }
    };

    const preprocessData = (data) => {
        const grouped = {};
        const noCostOrAccount = [];
        const totalsByType = {};

        data.forEach((item) => {
            // Assign default Cost_Type if missing and Resource_Type is 'Labor Rate'
            // if (!item.Cost_Type && item.Resource_Type === "Labor Rate") {
            //     item.Cost_Type = 51100;
            // }

            // Separate items with missing Cost_Type or Account_Code
            if (!item.Cost_Type) { // || !item.Account_Code) {
                noCostOrAccount.push(item);
                return;
            }

            const groupKey = phased
                ? `${item.Cost_Type}-${item.Subledger}`
                : `${item.Cost_Type}`;

            if (!grouped[groupKey]) {
                grouped[groupKey] = [];
            }
            grouped[groupKey].push(item);

            // Calculate totals for each Cost_Type
            if (!totalsByType[item.Cost_Type]) {
                totalsByType[item.Cost_Type] = 0;
            }
            totalsByType[item.Cost_Type] += pNum(item.Cost) || 0;
        });

        // Calculate totals for sections (e.g., 51*, 52*, etc.)
        const sectionTotals = {};
        Object.keys(totalsByType).forEach((costType) => {
            const sectionKey = costType.toString().substring(0, 2); // e.g., "51" for 51100
            if (!sectionTotals[sectionKey]) {
                sectionTotals[sectionKey] = 0;
            }
            sectionTotals[sectionKey] += pNum(totalsByType[costType]);
        });

        // Calculate grand total
        const grandTotal = Object.values(totalsByType).reduce((sum, val) => sum + pNum(val), 0);

        // Total for unassigned values
        const unassignedTotal = noCostOrAccount.reduce(
            (sum, item) => sum + (pNum(item.Cost) || 0),
            0
        );

        setGrandTotal(grandTotal);
        setGrouped(grouped);
        setNoCostType(noCostOrAccount);
        setTotalsByType(totalsByType);
        setSectionTotals(sectionTotals);
        setUnassignedTotal(unassignedTotal);
    };

    const toggleSection = (key) => {
        setExpandedSections((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const styles = StyleSheet.create({
        container: { flex: 1, padding: 10 },
        header: { fontSize: 18, fontWeight: "bold" },
        subHeader: { fontSize: 16, fontWeight: "600" },
        sectionHeader: { fontSize: 18, fontWeight: "bold", marginVertical: 5 },
        headerContainer: {
            flexDirection: "row",
            justifyContent: "space-between",
            marginVertical: 5,
        }, expandText: { fontSize: 16, color: "blue" },
        sectionTotal: { fontSize: 16, marginVertical: 3 },
        item: { padding: 10, borderWidth: 1, marginVertical: 5, borderRadius: 5 },
        container: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
        },
        loadingContainer: {
            marginTop: 20,
            alignItems: 'center',
        },
        loadingText: {
            marginTop: 10,
            fontSize: 16,
            color: '#333',
        },
        modalContainer: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
        },
        modalContent: {
            backgroundColor: '#fff',
            width: '90%',
            maxHeight: '80%',
            borderRadius: 10,
            padding: 20,
        },
        closeButton: {
            position: 'absolute',
            right: 10,
            top: 10,
        },
        closeButtonText: {
            fontSize: 18,
            fontWeight: 'bold',
            color: '#333',
        },
        modalTitle: {
            fontSize: 18,
            fontWeight: 'bold',
            marginBottom: 10,
            textAlign: 'center',
        },
        searchInput: {
            borderWidth: 1,
            borderColor: '#ddd',
            borderRadius: 8,
            padding: 10,
            marginBottom: 10,
        },
        jobItem: {
            padding: 15,
            borderBottomWidth: 1,
            borderBottomColor: '#ddd',
        },
        selectedJobItem: {
            backgroundColor: '#e0f7fa',
            color: 'red'
        },
        jobText: {
            fontSize: 16,
            color: '#000'
        },
        modalActions: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: 20,
        },
    });

    const s = (w = null) =>
        StyleSheet.create({
            spanH: {
                width: w ? w : 100,
                alignItems: 'center',
                fontSize: 13,
                fontWeight: '600',
                padding: 12,
                textAlign: 'center',
                borderBottomWidth: 2,
                borderBottomColor: '#2563eb',
                backgroundColor: '#f8fafc',
                color: '#1e293b',
            },
            spanTF: {
                width: w ? w : 100,
                alignItems: 'center',
                fontSize: 13,
                padding: 10,
                textAlign: 'right',
                justifyContent: 'flex-end',
                borderBottomWidth: 1,
                borderBottomColor: '#e2e8f0',
            },
        });

    const downloadTemplate = () => {
        // Create a link element
        // const link = document.createElement('a');
        // // Set the link's href to the file location in the public directory
        // link.href = process.env.PUBLIC_URL + '/dayZeroTemplate.xlsx';
        // // Set the download attribute with the desired file name
        // link.download = 'dayZeroTemplate.xlsx';
        // // Append the link to the document body (required for Firefox)
        // document.body.appendChild(link);
        // // Programmatically click the link to trigger the download
        // link.click();
        // // Remove the link from the document
        // document.body.removeChild(link);
        
        // Cost type descriptions mapping
        const costTypeDescriptions = {
            "51100": "Regular Time",
            "51200": "Overtime",
            "51300": "Burden",
            "51400": "BYOD",
            "51500": "Per Diem",
            "51600": "Truck Pay",
            "51700": "Welding Rig Pay",
            "51800": "Field Bonus",
            "52100": "Inside Equipment Rental",
            "52200": "Outside Equipment Rental",
            "52300": "Fuel",
            "52400": "Repairs",
            "53100": "Materials",
            "53200": "Mats",
            "53210": "Mats",
            "53300": "Tools & Supplies",
            "53400": "Skids",
            "54100": "Internal Subcontractors",
            "54200": "External Subcontractors",
            "54300": "Hauling and Transportation",
            "55100": "Personal Meals",
            "55200": "Team Meals/Entertain",
            "55300": "Airfare",
            "55400": "Lodging",
            "56100": "Field Office Rent",
            "56200": "Field Office Utility",
            "56300": "Field Office Expense",
            "56400": "Lab Fees & Testing",
            "56500": "Safety & PPE",
            "56600": "Bonds and Insurance Premiums",
            "56700": "Permits",
            "56800": "Insurance Claims",
            "57100": "Taxes",
            "57200": "Accrued Expense",
            "57300": "Warranty Reserve",
            "57900": "Miscellaneous",
            "55900": "Miscellaneous",
            "11420": "Miscellaneous",
            "59899": "Miscellaneous"
        };

        const worksheetData = [
            ['Cost Code', 'Phase Description', 'Cost Type', 'Cost Type Description', 'Amount', 'Billable Cost', 'Hours', 'Quantities', 'UoM', 'Resource Code']
        ];

        const excelRows = []
        rowList.forEach((job, i) => {
            excelRows.push({
                'Cost Code': job['Cost_Code'],
                'Phase Description': phaseCodes[job['Cost_Code']] || '',
                'Cost Type': job['Cost_Type'],
                'Cost Type Description': costTypeDescriptions[job['Cost_Type']] || '',
                'Amount': job['Value_Amount'],
                'Billable Cost': billable.amounts[i] ? Math.round(billable.amounts[i] * 100) / 100 : '',
                'Hours': job['Hours'],
                'Quantities': job['Quantity'],
                'UoM': job['UoM'] || '',
                'Resource Code': job['Resource_Code']
            })
        })

        // Create a new workbook and add a worksheet
        const workbook = XLSX.utils.book_new();
        let worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        if (rowList.length) {
            worksheet = XLSX.utils.json_to_sheet(excelRows);
        }

        // Append the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        // Generate the Excel file and trigger the download
        XLSX.writeFile(workbook, 'dayZeroTemplate.xlsx');
    };

    const uploadUserData = (file = null) => {
        console.log('upload excel', file)
        if (file) {

            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // Assuming the first row contains column headers
                // You can customize this according to your Excel file structure
                const desiredColumns = [
                    'Version',
                    'Forecast_User_Data_GUID',
                    'DSS_Created_By',
                    'DSS_Created_Datetime',
                    'DSS_Updated_By',
                    'DSS_Updated_Datetime',
                    'User_Name',
                    'User_ID',
                    'Forecast_Field_Name',
                    'Forecast_Version',
                    'Forecast_Tab',
                    'Forecast_Row_ID',
                    'Seth_Value',
                    'Cost_Type',
                    'Cost_Code',
                    'Supplier_Name',
                    'PO_Number',
                    'Equipment_Tier',
                    'BU_ID',
                    'Forecast_Datetime',
                    'Project_Start',
                    'Project_End',
                    'Phase_Start',
                    'Phase_End',
                    'Rate',
                    'Number_Hours',
                    'Number_Units',
                    'Active',
                    'Value_Amount',
                    'Hours',
                    'Quantity',
                    'Description',
                    'Notes',
                    'Resource_Code',
                    'Status',
                    'Row_Label'
                ];
                //const desiredColumns = ['BU ID', 'Equipment Tier', 'PO Number', 'Supplier Name', 'Cost Code', 'Cost Type', 'Forecast Field Name', 'Forecast Tab', 'Forecast Row ID', 'Value', 'Version'];
                const columnIndexMap = {};
                const parsedColumns = parsedData[0];
                desiredColumns.forEach((col) => {
                    const index = parsedColumns.indexOf(col);
                    if (index !== -1) {
                        columnIndexMap[col] = index;
                    }
                });

                const extractedData = parsedData.slice(1).map((row) => {
                    const rowData = {};
                    desiredColumns.forEach((col) => {
                        rowData[col] = row[columnIndexMap[col]];
                    });
                    return rowData;
                });

                console.log('extracted data', extractedData);
                const lineRows = {}

                const saveRowUserData = (val, key, row, version, tab, id, equipmentTier, poNumber, supplierName, costCode, costType) => {
                    return {
                        userId: userId || 'seth.bailey@strikeusa.com',
                        userName: userName || 'Seth Bailey',
                        fieldName: key ? (key + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 50) : '',
                        tab: 'Overhead',//tab ? (tab + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 50) : '',
                        rowId: row ? (row + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 80) : '',
                        version: version,//version ? (version + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0,50) : '',
                        value: val ? (val + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 1000) : '',
                        id: id ? (id + '').replace(/[^a-zA-Z0-9.\/ \-\^&()*=]/g, '').substring(0, 10) : '',
                        equipment: equipmentTier ? (equipmentTier + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 50) : '',
                        po: poNumber ? (poNumber + '').replace(/[^a-zA-Z0-9.\/ \-\^&()*=]/g, '').substring(0, 10) : '',
                        vendor: supplierName ? (supplierName + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 50) : '',
                        costCode: costCode ? (costCode + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 10) : '',
                        costType: costType ? (costType + '').replace(/[^a-zA-Z0-9.\/ \-\^~&()*=]/g, '').substring(0, 10) : '',
                    }
                }

                const rowsToSave = []
                // const combinations = {}
                // glValues?.forEach((d) => {
                //     if (!combinations[d.BU_ID]) {
                //         combinations[d.BU_ID] = {}
                //     }

                //     if (!combinations[d.BU_ID][d.Object_Account_ID]) {
                //         combinations[d.BU_ID][d.Object_Account_ID] = {}
                //     }

                //     if (!combinations[d.BU_ID][d.Object_Account_ID][d.Subsidiary]) {
                //         combinations[d.BU_ID][d.Object_Account_ID][d.Subsidiary] = true
                //     }
                // })
                // console.log('combinations', combinations)
                const notFound = []
                extractedData.forEach((row, index) => {
                    //do validation on whether combination exists
                    // if (!combinations[row.jobId]?.[row.costType]?.[row.phaseCode]) {
                    //     notFound.push(row)
                    // } else {
                    rowsToSave.push(saveRowUserData(row['Seth_Value'], row['Forecast_Field_Name'], row['Forecast_Row_ID'], row['Version'], row['Forecast Tab'], row['BU ID'], row['Equipment Tier'], row['PO Number'], row['Supplier Name'], row['Cost Code'], row['Cost Type']))

                    //     lineRows[index] = row
                    // }
                })
                //console.log('not found', notFound)
                console.log('row to save', rowsToSave)
                updateUserDataArray(rowsToSave)
            };
            reader.readAsArrayBuffer(file);
        }
    }

    const uploadExcel = (file = null) => {
        //console.log('upload excel', file)
        // A locked version skips every save, so clearing its Day Zero first
        // would leave it empty. Stop before anything is deleted.
        if (file && lockedVersion) {
            setRevenueStatus({ kind: 'error', text: 'This version is locked; nothing was uploaded. Upload to the live version.' });
            if (fileInput.current) fileInput.current.value = '';
            return;
        }
        if (file && uploading.current) {
            if (fileInput.current) fileInput.current.value = '';
            return;
        }
        if (file) {

            const reader = new FileReader();

            console.log('glvalues', glValues)
            const combinations = {}
            glValues?.forEach((d) => {
                d.BU_ID = d.BU_ID.trim();
                d.Object_Account_ID = d.Cost_Code?.trim();
                d.Subsidiary = d.Cost_Type?.trim();
                if (!combinations[d.BU_ID]) {
                    combinations[d.BU_ID] = {}
                }

                if (!combinations[d.BU_ID][d.Object_Account_ID]) {
                    combinations[d.BU_ID][d.Object_Account_ID] = {}
                }

                if (!combinations[d.BU_ID][d.Object_Account_ID][d.Subsidiary]) {
                    combinations[d.BU_ID][d.Object_Account_ID][d.Subsidiary] = true
                }
            })
            console.log('combinations', combinations)


            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const desiredColumns = ['Cost Code', 'Cost Code Description', 'Cost Type', 'Cost Type Description', 'Amount', 'Hours', 'Quantities', 'UoM', 'Resource Code'];
                const requiredColumns = ['Cost Code', 'Cost Type', 'Amount'];
                // Pick the first sheet whose header row contains all required columns;
                // fall back to SheetNames[0] so behavior is unchanged for single-sheet files.
                let chosenSheet = workbook.SheetNames[0];
                let parsedData = XLSX.utils.sheet_to_json(workbook.Sheets[chosenSheet], { header: 1 });
                const hasAllRequired = (hdr) => requiredColumns.every((c) => (hdr || []).indexOf(c) !== -1);
                if (!hasAllRequired(parsedData[0])) {
                    for (const n of workbook.SheetNames) {
                        const p = XLSX.utils.sheet_to_json(workbook.Sheets[n], { header: 1 });
                        if (hasAllRequired(p[0])) { chosenSheet = n; parsedData = p; break; }
                    }
                }

                const columnIndexMap = {};
                const parsedColumns = parsedData[0] || [];
                desiredColumns.forEach((col) => {
                    const index = parsedColumns.indexOf(col);
                    if (index !== -1) {
                        columnIndexMap[col] = index;
                    }
                });

                // Optional Billable Cost column: what each line bills the client.
                const revenueCol = DAY_ZERO_REVENUE_HEADERS.map((h) => parsedColumns.indexOf(h)).find((i) => i !== -1);
                const hasRevenueCol = revenueCol !== undefined;

                // Blank rows inside the sheet's used range (formatting, a column
                // formula) are not budget lines. On 10/8/2026 a sheet carried
                // tens of thousands of them and the write crashed the service.
                const extractedData = dropBlankRows(parsedData.slice(1).map((row) => {
                    const rowData = {};
                    desiredColumns.forEach((col) => {
                        rowData[col] = row[columnIndexMap[col]];
                    });
                    if (hasRevenueCol) rowData['Revenue'] = row[revenueCol];
                    return rowData;
                }));

                console.log('extractedData', extractedData);
                const stopUpload = (text) => {
                    setRevenueStatus({ kind: 'error', text });
                    if (fileInput.current) fileInput.current.value = '';
                };
                if (!extractedData.length) {
                    stopUpload(`No budget lines found on sheet "${chosenSheet}"; nothing was changed. The sheet needs Cost Code, Cost Type and Amount columns.`);
                    return;
                }
                if (extractedData.length > MAX_UPLOAD_ROWS) {
                    stopUpload(`The sheet has ${extractedData.length.toLocaleString()} lines, more than the ${MAX_UPLOAD_ROWS.toLocaleString()} an upload can take; nothing was changed. Check for rows below the budget.`);
                    return;
                }

                const notFoundCombinations = [];
                const lineRows = {}
                let outsideIndex = 0
                const foundCombinations = [];
                extractedData.forEach((row) => {
                    const costType = row['Cost Type'];
                    const phaseCode = row['Cost Code'];

                    if (!combinations[selectedJob]?.[costType]?.[phaseCode] && costType != '60900') {
                        if (row['Amount']) {
                            notFoundCombinations.push({ selectedJob, costType, phaseCode });
                            lineRows[outsideIndex] = {
                                Cost_Code: row['Cost Code'],
                                Cost_Type: row['Cost Type'],
                                Value_Amount: row['Amount'] ? pNum(row['Amount']) + '' : '',
                                Hours: row['Hours'] ? pNum(row['Hours']) + '' : '',
                                Quantity: row['Quantities'] ? pNum(row['Quantities']) + '' : '',
                                UoM: row['UoM'] ? String(row['UoM']).trim() : '',
                                notFound: true,
                                Resource_Code: row['Resource Code'] ? row['Resource Code'] : '',
                                Revenue: row['Revenue'] ? pNum(row['Revenue']) + '' : ''
                            }
                            outsideIndex++
                        } else {
                            foundCombinations.push({
                                Cost_Code: row['Cost Code'],
                                Cost_Type: row['Cost Type'],
                                Value_Amount: row['Amount'] ? pNum(row['Amount']) + '' : '',
                                Hours: row['Hours'] ? pNum(row['Hours']) + '' : '',
                                Quantity: row['Quantities'] ? pNum(row['Quantities']) + '' : '',
                                UoM: row['UoM'] ? String(row['UoM']).trim() : '',
                                Resource_Code: row['Resource Code'] ? row['Resource Code'] : '',
                                Revenue: row['Revenue'] ? pNum(row['Revenue']) + '' : ''
                            })
                        }
                    } else {
                        console.log('found', selectedJob, costType, phaseCode)
                        foundCombinations.push({
                            Cost_Code: row['Cost Code'],
                            Cost_Type: row['Cost Type'],
                            Value_Amount: row['Amount'] ? pNum(row['Amount']) + '' : '',
                            Hours: row['Hours'] ? pNum(row['Hours']) + '' : '',
                            Quantity: row['Quantities'] ? pNum(row['Quantities']) + '' : '',
                            UoM: row['UoM'] ? String(row['UoM']).trim() : '',
                            Resource_Code: row['Resource Code'] ? row['Resource Code'] : '',
                            Revenue: row['Revenue'] ? pNum(row['Revenue']) + '' : ''
                        })
                    }
                });

                console.log('Found Combinations:', foundCombinations);

                // console.log('extracted data', extractedData);


                if (uploading.current) {
                    if (fileInput.current) fileInput.current.value = '';
                    return;
                }
                uploading.current = true;
                setUploadBusy(true);
                const seconds = Math.max(10, Math.ceil(foundCombinations.length * 0.045 / 10) * 10);
                setRevenueStatus({ kind: 'info', text: `Uploading ${foundCombinations.length.toLocaleString()} lines; this takes about ${seconds} seconds. Keep this page open until it says the budget is uploaded.` });
                const warnLeave = (e) => { e.preventDefault(); e.returnValue = ''; };
                window.addEventListener('beforeunload', warnLeave);

                (async () => {
                    // The weekly version build copies the selected version at its
                    // start; an upload during the build, or onto a version that is
                    // no longer the newest, is left behind. Ask for a fresh list.
                    // An admin unlock on this version means the older version
                    // is the one meant; only a running build stops it then.
                    let block = null;
                    try {
                        const fresh = await loadVersionTeamJob({ forecastGuid: selectedVersion });
                        const unlocked = !!activeVersionUnlock(versionUnlocks, selectedVersion, selectedJob);
                        block = versionBuildBlock(fresh?.version, selectedVersion, new Date(), { unlocked });
                    } catch (err) {
                        console.log('version check skipped', err);
                    }
                    if (block) { stopUpload(block); return; }

                    const rowsToSave = []
                    foundCombinations.forEach((row, index) => {
                        rowsToSave.push({
                            userId: userId || 'seth.bailey@strikeusa.com',
                            userName: userName || 'Seth Bailey',
                            fieldName: 'dayZeroRow',
                            tab: "Day Zero Budget",
                            rowId: index,
                            version: selectedVersion + '',
                            value: row['Value_Amount'],
                            id: selectedJob,
                            lockedVersion: lockedVersion,
                            insertRow: 1,
                            costCode: sqlSafeText(row['Cost_Code']),
                            costType: sqlSafeText(row['Cost_Type']),
                            valueAmount: row['Value_Amount'] ? pNum(row['Value_Amount']) + '' : '',
                            hours: row['Hours'] ? pNum(row['Hours']) + '' : '',
                            quantity: row['Quantity'] ? pNum(row['Quantity']) + '' : '',
                            uom: row['UoM'] ? sqlSafeText(String(row['UoM']).trim().slice(0, 16)) : '',
                            resourceCode: sqlSafeText(row['Resource_Code'])
                        })
                        lineRows[outsideIndex + index] = { ...row, Forecast_Row_ID: String(index) }
                    })
                    console.log('line rows', rowsToSave)

                    // Write the new lines first, then clear the lines that were there
                    // before them. A write that fails leaves the previous budget in
                    // place instead of an empty tab (10/8/2026: three uploads died
                    // after the delete and the version sat empty between retries).
                    const guids = (await updateUserDataArray(rowsToSave)).map(Number).filter((g) => Number.isFinite(g) && g > 0);
                    if (rowsToSave.length && guids.length < rowsToSave.length) {
                        if (guids.length) await deleteDayZero({ id: selectedJob, forecastGuid: selectedVersion + '', fromGuid: Math.min(...guids) });
                        stopUpload(`Only ${guids.length} of ${rowsToSave.length} lines could be saved, so the previous budget was kept. Try the upload again.`);
                        return;
                    }
                    const cleared = await deleteDayZero({ id: selectedJob, forecastGuid: selectedVersion + '', beforeGuid: guids.length ? Math.min(...guids) : undefined })
                    // The clear must be seen to have run: a failed one leaves the
                    // version carrying both the old and the new lines (10/8/2026,
                    // 54026071 v124 showed a doubled budget for an hour).
                    if (!cleared || cleared.error || typeof cleared.deleted !== 'number') {
                        stopUpload(`The new lines were saved but the previous lines could not be cleared${cleared?.error ? ` (${cleared.error})` : ''}, so the version now holds both. Upload the file again; a second upload clears everything before it.`);
                        return;
                    }
                    unsavedRows.current = Object.values(lineRows).filter((r) => r.notFound)
                    setRows(lineRows)

                    // Use bulk upload instead of uploadToJDE
                    // syncBulkUploadToJDE(foundCombinations);

                    // A Billable Cost column with amounts replaces the version's Revenue Budget,
                    // one line per Day Zero row; without one the Revenue Budget is left as is.
                    const revLines = hasRevenueCol ? dayZeroRevenueLines(foundCombinations, (cc) => phaseCodes?.[cc]) : [];
                    if (revLines.length && lockedVersion) {
                        setRevenueStatus({ kind: 'error', text: 'This version is locked; the Billable Cost column was not saved.' });
                    } else if (revLines.length) {
                        try {
                            const { saves, deletes } = revenueBudgetSaves({ rows: revLines, existing: jobUserData, job: selectedJob, version: selectedVersion, lockedVersion, userId, userName });
                            await updateUserDataArray(saves.map((s) => ({ ...s, costCode: sqlSafeText(s.costCode), costType: sqlSafeText(s.costType), description: sqlSafeText(s.description) })));
                            for (const d of deletes) await deleteUserData(d);
                            const total = revLines.reduce((a, l) => a + l.amount, 0);
                            const lump = foundCombinations.filter((r) => String(r.Cost_Type || '').startsWith('4')).reduce((a, r) => a + pNum(r.Value_Amount || 0), 0);
                            const ties = Math.abs(total - lump) < 1;
                            const tie = !lump ? 'no 4xxxx revenue line to tie to'
                                : ties ? 'ties to the 4xxxx revenue line'
                                : `${total > lump ? 'over' : 'under'} the 4xxxx revenue line ($${fNum(lump, 1)}) by $${fNum(Math.abs(total - lump), 1)}`;
                            setRevenueStatus({ kind: ties ? 'ok' : 'warn', text: `Billable cost saved on ${revLines.length} lines, $${fNum(total, 1)}: ${tie}.` });
                        } catch (err) {
                            setRevenueStatus({ kind: 'error', text: `Billable cost didn't save: ${err.message || err}` });
                        }
                    } else {
                        setRevenueStatus({ kind: 'ok', text: `Budget uploaded: ${rowsToSave.length.toLocaleString()} lines.` });
                    }
                    const fresh = await loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion });
                    if (fresh?.data) setJobUserData(fresh.data);
                })().catch((err) => {
                    console.error('day zero upload failed', err);
                    stopUpload(`The upload stopped: ${err?.message || err}. The previous budget was kept.`);
                }).finally(() => {
                    uploading.current = false;
                    setUploadBusy(false);
                    window.removeEventListener('beforeunload', warnLeave);
                    if (fileInput.current) fileInput.current.value = '';
                });
            };
            reader.readAsArrayBuffer(file);
        }
    }

    const fileSelectedHandler = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        uploadExcel(selectedFile);
        //uploadUserData(selectedFile);
    }

    const HeaderRow = () => {
        return (
            <View style={{ flexDirection: 'row', position: 'sticky', top: 0, zIndex: 10 }}>
                <Text style={s(150).spanH}>Cost Code</Text>
                <Text style={s(150).spanH}>Cost Code Description</Text>
                <Text style={s(150).spanH}>Cost Type</Text>
                <Text style={s(150).spanH}>Cost Type Description</Text>
                <Text style={s(120).spanH}>Amount</Text>
                {billable.has && <Text style={s(120).spanH}>Billable Cost</Text>}
                <Text style={s(100).spanH}>Hours</Text>
                <Text style={s(100).spanH}>Quantities</Text>
                <Text style={s(120).spanH}>Resource Code</Text>
            </View>
        )
    }

    const Row = (props) => {
        const row = props.row
        const [costCode, setCostCode] = useState(row && row['Cost_Code'] ? row['Cost_Code'] : '');
        const [costType, setCostType] = useState(row && row['Cost_Type'] ? row['Cost_Type'] : '');
        const [amount, setAmount] = useState(row && row['Value_Amount'] ? fNum(row['Value_Amount']) : '');
        const [hours, setHours] = useState(row && row['Hours'] ? fNum(row['Hours']) : '');
        const [quantities, setQuantities] = useState(row && row['Quantity'] ? fNum(row['Quantity']) : '');
        const uom = row && row['UoM'] ? String(row['UoM']).trim() : '';
        const [resourceCode, setResourceCode] = useState(row && row['Resource_Code'] ? row['Resource_Code'] : '');

        // Cost type descriptions mapping
        const costTypeDescriptions = {
            "51100": "Regular Time", "51200": "Overtime", "51300": "Burden", "51400": "BYOD", "51500": "Per Diem",
            "51600": "Truck Pay", "51700": "Welding Rig Pay", "51800": "Field Bonus", "52100": "Inside Equipment Rental",
            "52200": "Outside Equipment Rental", "52300": "Fuel", "52400": "Repairs", "53100": "Materials",
            "53200": "Mats", "53210": "Mats", "53300": "Tools & Supplies", "53400": "Skids",
            "54100": "Internal Subcontractors", "54200": "External Subcontractors", "54300": "Hauling and Transportation",
            "55100": "Personal Meals", "55200": "Team Meals/Entertain", "55300": "Airfare", "55400": "Lodging",
            "56100": "Field Office Rent", "56200": "Field Office Utility", "56300": "Field Office Expense",
            "56400": "Lab Fees & Testing", "56500": "Safety & PPE", "56600": "Bonds and Insurance Premiums",
            "56700": "Permits", "56800": "Insurance Claims", "57100": "Taxes", "57200": "Accrued Expense",
            "57300": "Warranty Reserve", "57900": "Miscellaneous", "55900": "Miscellaneous", "11420": "Miscellaneous", "59899": "Miscellaneous"
        };

        return (
            <View style={{ 
                flexDirection: 'row', 
                backgroundColor: row?.notFound ? '#fee2e2' : (props.index % 2 === 0 ? '#ffffff' : '#f9fafb'),
            }}>
                <Text style={[s(150).spanTF, { color: row?.notFound ? '#dc2626' : '#334155' }]}>{costCode}</Text>
                <Text style={[s(150).spanTF, { color: row?.notFound ? '#dc2626' : '#334155' }]}>{phaseCodes[costCode]}</Text>
                <Text style={[s(150).spanTF, { color: row?.notFound ? '#dc2626' : '#334155' }]}>{costType}</Text>
                <Text style={[s(150).spanTF, { color: row?.notFound ? '#dc2626' : '#64748b' }]}>{costTypeDescriptions[costType] || ''}</Text>
                <Text style={[s(120).spanTF, { color: row?.notFound ? '#dc2626' : '#0f172a', fontWeight: '500' }]}>{amount}</Text>
                {billable.has && <Text style={[s(120).spanTF, { color: row?.notFound ? '#dc2626' : '#0f172a' }]}>{row?.notFound ? (row.Revenue ? fNum(row.Revenue) : '') : (props.revenue ? fNum(props.revenue) : '')}</Text>}
                <Text style={[s(100).spanTF, { color: row?.notFound ? '#dc2626' : '#334155' }]}>{hours}</Text>
                <Text style={[s(100).spanTF, { color: row?.notFound ? '#dc2626' : '#334155' }]}>{quantities}{quantities && uom ? ` ${uom}` : ''}</Text>
                <Text style={[s(120).spanTF, { color: row?.notFound ? '#dc2626' : '#334155' }]}>{resourceCode}</Text>
                {row?.notFound ? !costCode ? <Text style={[s(150).spanH, { backgroundColor: '#fee2e2', color: '#dc2626', fontSize: 12 }]}>*JDE Combination not found. Maybe use project team 100101?</Text> : <Text style={[s(150).spanH, { backgroundColor: '#fee2e2', color: '#dc2626', fontSize: 12 }]}>*Not saved. Combination in JDE doesn't exist </Text> : null}
            </View>
        )
    }

    // ---- Budget Overview dashboard helpers ----
    const DASH_PALETTE = ['#2563eb', '#0ea5e9', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#6366f1', '#475569'];

    const kpiIconPaths = {
        dollar: <><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
        clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
        box: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></>,
        users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
        grid: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>,
        tag: <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>,
        trend: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>,
    };

    const KpiIcon = ({ name }) => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{kpiIconPaths[name]}</svg>
    );

    const DonutBreakdown = ({ title, entries, total, prefix = '$' }) => {
        const data = {
            labels: entries.map((e) => e.label),
            datasets: [{ data: entries.map((e) => e.value), backgroundColor: entries.map((e) => e.color), borderWidth: 0, hoverOffset: 4 }],
        };
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '62%',
            plugins: {
                legend: { display: false },
                tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${prefix}${fNum(ctx.parsed, 1)}` } },
            },
        };
        return (
            <View style={{ flex: 1, minWidth: 340, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F2A4A', marginBottom: 12 }}>{title}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                    <View style={{ width: 140, height: 140, position: 'relative', flexShrink: 0 }}>
                        <Doughnut data={data} options={options} />
                        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <Text style={{ fontSize: 9, color: '#94a3b8', fontWeight: '700', letterSpacing: 0.5 }}>TOTAL</Text>
                            <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{prefix}{fNum(total, 1)}</Text>
                        </View>
                    </View>
                    <View style={{ flex: 1 }}>
                        {entries.map((e) => {
                            const pct = total ? (e.value / total) * 100 : 0;
                            return (
                                <View key={e.label} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                                    <View style={{ width: 9, height: 9, borderRadius: 2, backgroundColor: e.color, marginRight: 8, flexShrink: 0 }} />
                                    <Text style={{ flex: 1, fontSize: 12, color: '#334155' }} numberOfLines={1}>{e.label}</Text>
                                    <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600', marginLeft: 8 }}>{prefix}{fNum(e.value, 1)}</Text>
                                    <Text style={{ fontSize: 12, color: '#64748b', width: 46, textAlign: 'right' }}>{pct.toFixed(1)}%</Text>
                                </View>
                            );
                        })}
                    </View>
                </View>
            </View>
        );
    };

    return (
        <View style={{ 
            margin: 30, 
            marginBottom: 200, 
            backgroundColor: 'white', 
            borderRadius: 8, 
            padding: 24,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            border: '1px solid #e2e8f0'
        }}>
                {/* VoltaGrid: the cost budget and its revenue breakout are two views of the one screen */}
                {isVg && (
                    <div style={{ display: 'inline-flex', alignSelf: 'flex-start', marginBottom: 20, border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden' }}>
                        {[['cost', 'Cost Budget'], ['revenue', 'Revenue Budget']].map(([k, label]) => (
                            <button key={k} type="button" onClick={() => setBudgetView(k)}
                                style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                                    background: budgetView === k ? '#2563eb' : '#f8fafc', color: budgetView === k ? '#fff' : '#334155' }}>
                                {label}
                            </button>
                        ))}
                    </div>
                )}
                {isVg && budgetView === 'revenue' ? <RevenueBudget /> : (<>
                {/* Budget Overview dashboard — summarizes the uploaded budget above the upload section (Seth, Steve, Troy while WIP) */}
                {canSeeBudgetOverview && rows && Object.keys(rows).length > 0 && (() => {
                    // Series colours in their fixed order (docs/STRIKE_STYLE.md, section 7); "Other" is the grouped grey
                    const lemsoColors = { Labor: SERIES[0], Equipment: SERIES[1], Materials: SERIES[2], Subs: SERIES[3], Contingency: SERIES[4], Other: SERIES_OTHER };

                    const lemso = { Labor: 0, Equipment: 0, Materials: 0, Subs: 0, Other: 0, Contingency: 0 };
                    const lemsoHrs = { Labor: 0, Equipment: 0, Materials: 0, Subs: 0, Other: 0 };
                    const hoursByDiscipline = {};  // discipline -> hours
                    const byDiscipline = {};      // discipline description -> amount
                    const byResourceAmt = {};     // resource code -> cost amount
                    const byResourceQty = {};     // resource code -> quantity
                    const costCodeSet = new Set();
                    const costTypeSet = new Set();
                    const resourceSet = new Set();
                    let totalHours = 0;
                    let totalQty = 0;
                    let revenue = 0;

                    Object.values(rows).forEach((r) => {
                        const ct = (r.Cost_Type || '').toString();
                        const cc = (r.Cost_Code || '').toString();
                        const rc = (r.Resource_Code || '').toString().trim();
                        const val = pNum(r.Value_Amount || 0);
                        const hrs = parseFloat(r.Hours) || 0;
                        const qty = parseFloat(r.Quantity) || 0;
                        if (cc) costCodeSet.add(cc);
                        if (ct) costTypeSet.add(ct);
                        if (rc) resourceSet.add(rc);
                        totalHours += hrs;
                        totalQty += qty;
                        if (qty) byResourceQty[rc || 'Unassigned'] = (byResourceQty[rc || 'Unassigned'] || 0) + qty;

                        // Mirror the side menu's Day Zero budget categorization so the numbers match.
                        // Cost code 101101 is contingency (kept out of the cost buckets); "Other" is 55000-58000.
                        const isContingency = cc === '101101';

                        // Cost-type family (used for hours bucketing regardless of contingency).
                        let family = null;
                        if (ct >= '51000' && ct <= '51999') family = 'Labor';
                        else if (ct >= '52000' && ct <= '52999') family = 'Equipment';
                        else if (ct >= '53000' && ct <= '53999') family = 'Materials';
                        else if (ct >= '54000' && ct <= '54999') family = 'Subs';
                        else if (ct >= '55000' && ct <= '58000') family = 'Other';

                        if (hrs) {
                            const hDisc = disciplineMap[cc] || 'Unassigned';
                            hoursByDiscipline[hDisc] = (hoursByDiscipline[hDisc] || 0) + hrs;
                        }

                        let bucket = null;
                        if (isContingency) {
                            if (ct[0] === '5') bucket = 'Contingency';
                        } else {
                            bucket = family;
                        }

                        if (bucket) {
                            lemso[bucket] += val;
                            if (bucket === 'Labor' || bucket === 'Equipment') lemsoHrs[bucket] += hrs;
                            // Discipline / resource rollups exclude contingency (101101 isn't a real phase).
                            if (bucket !== 'Contingency') {
                                const disc = disciplineMap[cc] || 'Unassigned';
                                byDiscipline[disc] = (byDiscipline[disc] || 0) + val;
                                byResourceAmt[rc || 'Unassigned'] = (byResourceAmt[rc || 'Unassigned'] || 0) + val;
                            }
                        } else if (!isContingency && ct[0] === '4') {
                            // Revenue = the sum of the 4xxxx lines, as entered (e.g. 41000 Contract Revenues Earned).
                            revenue += val;
                        }
                    });

                    const totalBudget = lemso.Labor + lemso.Equipment + lemso.Materials + lemso.Subs + lemso.Other + lemso.Contingency;

                    // Build top-N donut entries from a { key: value } map, rolling the tail into "Other".
                    const buildEntries = (map, topN) => {
                        const arr = Object.keys(map)
                            .map((k) => ({ label: k, value: map[k] }))
                            .filter((e) => e.value !== 0)
                            .sort((a, b) => b.value - a.value);
                        if (arr.length <= topN) return arr.map((e, i) => ({ ...e, color: DASH_PALETTE[i % DASH_PALETTE.length] }));
                        const top = arr.slice(0, topN).map((e, i) => ({ ...e, color: DASH_PALETTE[i % DASH_PALETTE.length] }));
                        const rest = arr.slice(topN);
                        top.push({ label: 'Other (' + rest.length + ')', value: rest.reduce((s, e) => s + e.value, 0), color: '#cbd5e1' });
                        return top;
                    };

                    const lemsoEntries = [
                        { label: 'Labor', value: lemso.Labor, color: lemsoColors.Labor },
                        { label: 'Equipment', value: lemso.Equipment, color: lemsoColors.Equipment },
                        { label: 'Materials', value: lemso.Materials, color: lemsoColors.Materials },
                        { label: 'Subs', value: lemso.Subs, color: lemsoColors.Subs },
                        { label: 'Other', value: lemso.Other, color: lemsoColors.Other },
                        { label: 'Contingency', value: lemso.Contingency, color: lemsoColors.Contingency },
                    ].filter((e) => e.value !== 0);
                    const disciplineEntries = buildEntries(byDiscipline, 8);
                    const resourceEntries = buildEntries(byResourceAmt, 8);
                    // Discipline / resource donuts exclude contingency, so use their own sums for % and center total.
                    const disciplineTotal = disciplineEntries.reduce((s, e) => s + e.value, 0);
                    const resourceTotal = resourceEntries.reduce((s, e) => s + e.value, 0);

                    const hoursEntries = buildEntries(hoursByDiscipline, 8);
                    const qtyEntries = buildEntries(byResourceQty, 4);

                    const disciplineCount = Object.keys(byDiscipline).filter((k) => byDiscipline[k] !== 0).length;
                    const kpis = [
                        { label: 'Total Budget', value: '$' + fNum(totalBudget, 1), sub: '100% of total', icon: 'dollar' },
                        { label: 'Total Hours', value: fNum(totalHours, 1), sub: '', icon: 'clock' },
                        { label: 'Total Quantities', value: fNum(totalQty, 1), sub: '', icon: 'box' },
                        { label: 'Total Resources', value: resourceSet.size + '', sub: '', icon: 'users' },
                        { label: 'Total Cost Codes', value: costCodeSet.size + '', sub: 'Across ' + disciplineCount + ' disciplines', icon: 'grid' },
                        { label: 'Total Cost Types', value: costTypeSet.size + '', sub: '', icon: 'tag' },
                    ];
                    if (revenue > 0) {
                        const profit = revenue - totalBudget;
                        const gm = revenue ? (profit / revenue) * 100 : 0;
                        kpis.push({ label: 'Revenue', value: '$' + fNum(revenue, 1), sub: '', icon: 'dollar' });
                        kpis.push({ label: 'Job Profit', value: '$' + fNum(profit, 1), sub: gm.toFixed(1) + '% GM', icon: 'trend' });
                    }

                    const cardStyle = { flex: 1, minWidth: 340, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 };
                    const subHead = { fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 };
                    const miniBar = (key, label, value, total, color, prefix = '') => {
                        const pct = total ? (value / total) * 100 : 0;
                        const w = Math.min(100, Math.max(0, pct));
                        return (
                            <View key={key} style={{ marginBottom: 8 }}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 }}>
                                        <View style={{ width: 9, height: 9, borderRadius: 2, backgroundColor: color, flexShrink: 0 }} />
                                        <Text style={{ fontSize: 12, color: '#334155' }} numberOfLines={1}>{label}</Text>
                                    </View>
                                    <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>{prefix}{fNum(value, 1)} · {pct.toFixed(1)}%</Text>
                                </View>
                                <View style={{ height: 5, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                    <View style={{ height: '100%', width: w + '%', backgroundColor: color, borderRadius: 3 }} />
                                </View>
                            </View>
                        );
                    };

                    return (
                        <View style={{ marginBottom: 28 }}>
                            <Text style={{ fontSize: 18, fontWeight: '700', color: '#0F2A4A', marginBottom: 16 }}>Budget Overview</Text>

                            {/* KPI cards */}
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                                {kpis.map((k) => (
                                    <View key={k.label} style={{ flex: 1, minWidth: 140, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                            <View style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: '#eff6ff', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <KpiIcon name={k.icon} />
                                            </View>
                                            <Text style={{ flex: 1, fontSize: 10, color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</Text>
                                        </View>
                                        <Text style={{ fontSize: 17, fontWeight: '700', color: '#0f172a' }}>{k.value}</Text>
                                        {k.sub ? <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{k.sub}</Text> : null}
                                    </View>
                                ))}
                            </View>

                            {/* Breakdown cards */}
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
                                <DonutBreakdown title="Budget by Discipline (Rollup of Cost Codes)" entries={disciplineEntries} total={disciplineTotal} />
                                <DonutBreakdown title="Budget by Cost Type (LEMSO)" entries={lemsoEntries} total={totalBudget} />
                                {resourceEntries.length > 0 ? <DonutBreakdown title="Budget by Resource Category" entries={resourceEntries} total={resourceTotal} /> : null}

                                {/* Hours & Quantities Summary */}
                                <View style={cardStyle}>
                                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F2A4A', marginBottom: 12 }}>Hours & Quantities Summary</Text>
                                    <Text style={subHead}>Hours by Discipline</Text>
                                    {hoursEntries.length > 0
                                        ? hoursEntries.map((e) => miniBar('h-' + e.label, e.label, e.value, totalHours, e.color))
                                        : <Text style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>No hours</Text>}
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: '#e2e8f0', paddingTop: 6, marginBottom: 14 }}>
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0F2A4A' }}>Total</Text>
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0F2A4A' }}>{fNum(totalHours, 1)}</Text>
                                    </View>
                                    <Text style={subHead}>Quantities by Resource Category</Text>
                                    {qtyEntries.length > 0
                                        ? qtyEntries.map((e) => miniBar('q-' + e.label, e.label, e.value, totalQty, e.color))
                                        : <Text style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>No quantities</Text>}
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: '#e2e8f0', paddingTop: 6 }}>
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0F2A4A' }}>Total</Text>
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0F2A4A' }}>{fNum(totalQty, 1)}</Text>
                                    </View>
                                </View>
                            </View>
                        </View>
                    );
                })()}

                <View style={{ flexDirection: 'row', paddingBottom: 24, alignItems: 'center', gap: 12 }}>
                    {!budgetExists && (
                        <Pressable
                            style={{ 
                                alignItems: 'center', 
                                backgroundColor: '#2563eb', 
                                padding: 12, 
                                paddingHorizontal: 16,
                                borderRadius: 6, 
                                flexDirection: 'row',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                opacity: uploadBusy ? 0.6 : 1
                            }}
                            disabled={uploadBusy}
                            onPress={() => {
                                if (!uploadBusy) fileInput.current.click();
                            }}
                        >
                            <Text style={{ marginRight: 10, color: 'white', fontWeight: '500', fontSize: 14 }}>{uploadBusy ? 'Uploading…' : 'Upload Excel Budget'}</Text>
                            {uploadBusy
                                ? <ActivityIndicator size="small" color="white" />
                                : <Icon
                                    name="↑"
                                    size={18}
                                    color="white"
                                />}
                        </Pressable>
                    )}

                    {/* <Pressable
                        style={{
                            alignItems: 'center',
                            backgroundColor: isBulkUploading ? '#6c757d' : '#28a745',
                            padding: 10,
                            marginLeft: 10,
                            borderRadius: 5,
                            flexDirection: 'row',
                            opacity: isBulkUploading ? 0.6 : 1
                        }}
                        onPress={handleBulkUploadBudgets}
                        disabled={isBulkUploading}
                    >
                        <Text style={{ marginRight: 10, color: 'white', fontWeight: 'bold' }}>
                            {isBulkUploading ? 'Uploading...' : 'Bulk Upload Budgets'}
                        </Text>
                        <Icon
                            name={isBulkUploading ? "hourglass-empty" : "cloud-upload"}
                            size={20}
                            color="white"
                        />
                    </Pressable> */}

                {/* Bulk Upload Status Display */}
                {/* {bulkUploadStatus && (
                    <View style={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: 5,
                        padding: 15,
                        marginBottom: 20
                    }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                            <Icon2 
                                name={isBulkUploading ? "sync" : "check-circle"} 
                                size={20} 
                                color={isBulkUploading ? "#007bff" : "#28a745"} 
                                style={{ marginRight: 10 }}
                            />
                            <Text style={{ 
                                fontSize: 16, 
                                fontWeight: 'bold', 
                                color: isBulkUploading ? "#007bff" : "#28a745" 
                            }}>
                                Bulk Upload Status
                            </Text>
                        </View>
                        <Text style={{ fontSize: 14, marginBottom: 5 }}>{bulkUploadStatus}</Text>
                        {isBulkUploading && bulkUploadProgress.total > 0 && (
                            <View>
                                <Text style={{ fontSize: 12, color: '#6c757d' }}>
                                    Progress: {bulkUploadProgress.current} of {bulkUploadProgress.total} jobs
                                </Text>
                                <View style={{
                                    backgroundColor: '#e9ecef',
                                    height: 8,
                                    borderRadius: 4,
                                    marginTop: 5,
                                    overflow: 'hidden'
                                }}>
                                    <View style={{
                                        backgroundColor: '#007bff',
                                        height: '100%',
                                        width: `${(bulkUploadProgress.current / bulkUploadProgress.total) * 100}%`,
                                        transition: 'width 0.3s ease'
                                    }} />
                                </View>
                            </View>
                        )}
                    </View>
                )} */}

                {/* 
                    <Pressable
                        style={{
                            alignItems: 'center',
                            backgroundColor: '#007bff',
                            padding: 10,
                            marginLeft: 10,
                            borderRadius: 5,
                            flexDirection: 'row'
                        }}
                        onPress={handleSyncBudgets}
                        disabled={!data || data.length === 0}
                    >
                        <Text style={{ marginRight: 10, color: 'white', fontWeight: 'bold' }}>Sync Budgets</Text>
                        <Icon
                            name="sync"
                            size={20}
                            color="white"
                        />
                    </Pressable> */}

                    {/* <Pressable
                        style={{ alignItems: 'center', backgroundColor: '#e0e0e0', padding: 10, marginLeft: 10, borderRadius: 5, flexDirection: 'row' }}
                        onPress={() => {
                            importBudgetHardDollar()
                        }}
                        disabled={isLoading}
                    >
                        <Text style={{ marginRight: 10 }}>Import Hard Dollar Budget</Text>
                        {!isLoading ? <Icon
                            name="file-upload"
                            size={20}
                        /> :
                            <ActivityIndicator size="small" color="#192440" />
                        }
                    </Pressable> */}

                    <Modal visible={isModalVisible} animationType="slide" transparent={true}>
                        <View style={styles.modalContainer}>
                            <View style={styles.modalContent}>
                                <TouchableOpacity
                                    style={styles.closeButton}
                                    onPress={() => setIsModalVisible(false)}
                                >
                                    <Text style={styles.closeButtonText}>X</Text>
                                </TouchableOpacity>
                                {!selectedHardDollarJob ? <Text style={styles.modalTitle}>Select Hard Dollar Job</Text> : <Text style={styles.modalTitle}>Selected Job: {selectedHardDollarJob?.Code} - {selectedHardDollarJob?.Description}</Text>}
                                {!nextPageModal ? <TextInput
                                    style={styles.searchInput}
                                    placeholder="Search by code or description"
                                    value={searchText}
                                    onChangeText={handleSearch}
                                /> : null}
                                {!nextPageModal ? <FlatList
                                    data={filteredJobs}
                                    keyExtractor={(item) => item.Code}
                                    renderItem={({ item, index }) => (
                                        <TouchableOpacity
                                            key={index}
                                            style={[
                                                styles.jobItem,
                                                selectedHardDollarJob?.Code == item.Code ? styles.selectedJobItem : null,
                                                { flexDirection: 'row' }
                                            ]}
                                            onPress={() => setSelectedHardDollarJob(item)}
                                        >
                                            <Text style={[styles.jobText, {}]}>{item.Code} - </Text>
                                            <Text style={[styles.jobText, {}]}>{item.Description}</Text>
                                        </TouchableOpacity>
                                    )}
                                /> :
                                    <ScrollView>
                                        {/* {expandedSections["unassigned"] && (
                                            <FlatList
                                                data={noCostType}
                                                keyExtractor={(item, index) => `unassigned-${index}`}
                                                renderItem={({ item }) => (
                                                    <View style={styles.item}>
                                                        <Text>{item.Resource_Description}</Text>
                                                        <Text>Account Code: {item.Account_Code || "N/A"}</Text>
                                                        <Text>Cost Type: {item.Cost_Type || "N/A"}</Text>
                                                    </View>
                                                )}
                                            />
                                        )}

                                        {Object.keys(grouped).map((key) => (
                                            <View key={key}>
                                                <TouchableOpacity onPress={() => toggleSection(key)}>
                                                    <View style={styles.headerContainer}>
                                                        <Text style={styles.header}>{key}</Text>
                                                        <Text style={styles.header}>
                                                            $
                                                            {fNum(
                                                                grouped[key].reduce(
                                                                    (sum, item) => sum + (item.ZZZ_ERP_Actual_Cost || 0),
                                                                    0
                                                                )
                                                            )}
                                                        </Text>
                                                    </View>
                                                </TouchableOpacity>
                                                {expandedSections[key] && (
                                                    <FlatList
                                                        data={grouped[key]}
                                                        keyExtractor={(item, index) => `${key}-${index}`}
                                                        renderItem={({ item }) => (
                                                            <View style={styles.item}>
                                                                <Text>{item.Resource_Description}</Text>
                                                                <Text>Cost: ${fNum(item.ZZZ_ERP_Actual_Cost || 0)}</Text>
                                                            </View>
                                                        )}
                                                    />
                                                )}
                                            </View>
                                        ))} */}

                                        {/* Section Totals */}
                                        <Text style={styles.sectionHeader}>Section Totals:</Text>
                                        {Object.keys(sectionTotals).map((section) => (
                                            <View style={styles.headerContainer} key={section}>
                                                <Text style={styles.subHeader}>{section}*</Text>
                                                <Text style={styles.subHeader}>${fNum(sectionTotals[section])}</Text>
                                            </View>
                                        ))}

                                        {/* Unassigned Section */}
                                        <TouchableOpacity onPress={() => toggleSection("unassigned")}>
                                            <View style={styles.headerContainer}>
                                                <Text style={styles.subHeader}>
                                                    Unassigned
                                                </Text>
                                                <Text style={styles.subHeader}>${fNum(unassignedTotal)}</Text>
                                            </View>
                                        </TouchableOpacity>

                                        {/* Grand Total */}
                                        <View style={styles.headerContainer}>
                                            <Text style={styles.sectionHeader}>Grand Total</Text>
                                            <Text style={styles.sectionHeader}>${fNum(grandTotal)}</Text>
                                        </View>

                                    </ScrollView>}
                                <View style={styles.modalActions}>
                                    <Pressable
                                        style={{ alignItems: 'center', backgroundColor: 'grey', padding: 10, marginLeft: 10, borderRadius: 5, flexDirection: 'row' }}
                                        onPress={() => {
                                            setIsModalVisible(false)
                                        }}
                                    >
                                        <Text style={{ color: 'white' }}>Cancel</Text>
                                    </Pressable>
                                    <View style={{ flexDirection: 'row' }}>
                                        {waitingQuery ? <Text>[Takes up 30 secs]</Text> : null}
                                        <Pressable
                                            style={{ alignItems: 'center', backgroundColor: selectedJob ? 'blue' : 'grey', padding: 10, marginLeft: 10, borderRadius: 5, flexDirection: 'row' }}
                                            onPress={() => {
                                                handleContinue()
                                            }}
                                            disabled={waitingQuery}
                                        >
                                            <Text style={{ color: 'white' }}>Continue</Text>
                                            {waitingQuery ? <ActivityIndicator style={{ marginLeft: 10 }} size="small" color="white" /> : null}
                                        </Pressable>
                                    </View>
                                    {/* <Button
                                        title="Continue"
                                        onPress={handleContinue}
                                        disabled={!selectedJob}
                                        color={selectedJob ? 'blue' : 'grey'}
                                    /> */}
                                </View>
                            </View>
                        </View>
                    </Modal>
                    {/* <Pressable
                        style={{ alignItems: 'center', backgroundColor: '#e0e0e0', padding: 10, borderRadius: 5 }}
                        onPress={() => {
                            uploadExcel()
                            }}
                    >
                        <Icon
                            name="file-upload"
                            size={20}
                        />
                    </Pressable> */}
                    <Pressable
                        style={{ 
                            alignItems: 'center', 
                            backgroundColor: '#f8fafc', 
                            padding: 12,
                            paddingHorizontal: 16, 
                            borderRadius: 6, 
                            flexDirection: 'row',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                        onPress={() => {
                            downloadTemplate()
                        }}
                    >
                        <Text style={{ marginRight: 10, color: '#334155', fontWeight: '500', fontSize: 14 }}>Download Template</Text>
                        <Icon
                            name="↓"
                            size={18}
                            color="#334155"
                        />
                    </Pressable>
                    {/* <Pressable
                        style={{ alignItems: 'center', backgroundColor: '#e0e0e0', padding: 10, borderRadius: 5, marginLeft: 10, flexDirection: 'row' }}
                        onPress={() => {
                            uploadToJDE()
                        }}
                    >
                        <Text style={{ marginRight: 10 }}>Sync to JDE</Text>
                        <Icon
                            name="file-download"
                            size={20}
                        />
                    </Pressable>
                    <Pressable
                        style={{ alignItems: 'center', backgroundColor: '#28a745', padding: 10, borderRadius: 5, marginLeft: 10, flexDirection: 'row' }}
                        onPress={() => {
                            finishUploadToJDE()
                        }}
                    >
                        <Text style={{ marginRight: 10, color: 'white' }}>Finish Upload to JDE</Text>
                        <Icon
                            name="check-circle"
                            size={20}
                            color="white"
                        />
                    </Pressable> */}
                    <Text style={{ marginLeft: 16, color: '#64748b', fontSize: 13, fontStyle: 'italic' }}>*Budget upload should include revenue. Optional Billable Cost column: what each line bills the client.</Text>
                    <input type="file" ref={fileInput}
                        style={{ display: 'none', height: 0, width: 0 }}
                        onChange={fileSelectedHandler} accept=".xlsx" />
                </View>
                {revenueStatus && (
                    <Text style={{ marginBottom: 12, fontSize: 13, color: revenueStatus.kind === 'ok' ? '#15803d' : revenueStatus.kind === 'warn' ? '#b45309' : revenueStatus.kind === 'info' ? '#1d518c' : '#dc2626' }}>{revenueStatus.text}</Text>
                )}
                <DayZeroHistory />
                <HeaderRow />
                {notFound.length > 0 && (
                    <View style={{ marginTop: 20, marginBottom: 15 }}>
                        <View style={{ 
                            backgroundColor: '#fef2f2', 
                            borderLeftWidth: 4, 
                            borderLeftColor: '#dc2626',
                            padding: 12,
                            marginBottom: 10,
                            borderRadius: 4
                        }}>
                            <Text style={{ fontSize: 14, fontWeight: '600', color: '#dc2626' }}>Not Found ({notFound.length})</Text>
                            <Text style={{ fontSize: 12, color: '#991b1b', marginTop: 4 }}>These entries could not be validated in JDE</Text>
                        </View>
                        {notFound.map((row, index) => {
                            // Cost type descriptions mapping for not found section
                            const costTypeDescriptions = {
                                "51100": "Regular Time", "51200": "Overtime", "51300": "Burden", "51400": "BYOD", "51500": "Per Diem",
                                "51600": "Truck Pay", "51700": "Welding Rig Pay", "51800": "Field Bonus", "52100": "Inside Equipment Rental",
                                "52200": "Outside Equipment Rental", "52300": "Fuel", "52400": "Repairs", "53100": "Materials",
                                "53200": "Mats", "53210": "Mats", "53300": "Tools & Supplies", "53400": "Skids",
                                "54100": "Internal Subcontractors", "54200": "External Subcontractors", "54300": "Hauling and Transportation",
                                "55100": "Personal Meals", "55200": "Team Meals/Entertain", "55300": "Airfare", "55400": "Lodging",
                                "56100": "Field Office Rent", "56200": "Field Office Utility", "56300": "Field Office Expense",
                                "56400": "Lab Fees & Testing", "56500": "Safety & PPE", "56600": "Bonds and Insurance Premiums",
                                "56700": "Permits", "56800": "Insurance Claims", "57100": "Taxes", "57200": "Accrued Expense",
                                "57300": "Warranty Reserve", "57900": "Miscellaneous", "55900": "Miscellaneous", "11420": "Miscellaneous", "59899": "Miscellaneous"
                            };
                            
                            return (
                                <View key={`not-found-${index}`} style={{ flexDirection: 'row', backgroundColor: '#fee2e2', borderLeftWidth: 3, borderLeftColor: '#dc2626' }}>
                                    <Text style={[s(150).spanTF, { color: '#dc2626' }]}>{row['Cost Code']}</Text>
                                    <Text style={[s(150).spanTF, { color: '#dc2626' }]}>{phaseCodes[row['Cost Code']]}</Text>
                                    <Text style={[s(150).spanTF, { color: '#dc2626' }]}>{row['Cost Type']}</Text>
                                    <Text style={[s(150).spanTF, { color: '#64748b' }]}>{costTypeDescriptions[row['Cost Type']] || ''}</Text>
                                    <Text style={[s(120).spanTF, { color: '#dc2626', fontWeight: '500' }]}>{row['Amount']}</Text>
                                    {billable.has && <Text style={[s(120).spanTF, { color: '#dc2626' }]}>{row['Revenue']}</Text>}
                                    <Text style={[s(100).spanTF, { color: '#dc2626' }]}>{row['Hours']}</Text>
                                    <Text style={[s(100).spanTF, { color: '#dc2626' }]}>{row['Quantities']}{row['Quantities'] && row['UoM'] ? ` ${row['UoM']}` : ''}</Text>
                                    <Text style={[s(120).spanTF, { color: '#dc2626' }]}>{row['Resource Code']}</Text>
                                </View>
                            );
                        })}
                    </View>
                )}
                {rowList.map((row, index) => {
                    return (
                        <Row key={index} index={index} row={row} revenue={billable.amounts[index]} />
                    );
                })}
                <Row />
                {billable.unmatched.length > 0 && (
                    <Text style={{ marginTop: 10, fontSize: 12, color: '#b45309' }}>
                        Billable cost with no cost line on this budget: {billable.unmatched.map((u) => `${u.cc} / ${u.ct} $${fNum(u.amount, 1)}`).join(', ')}
                    </Text>
                )}

                {/* LEMSO Totals */}
                {rows && Object.keys(rows).length > 0 && (() => {
                    const lemsoKey = '__lemso__';
                    const lemso = { Labor: 0, Equipment: 0, Materials: 0, Subs: 0, Other: 0 };
                    const lemsoHrs = { Labor: 0, Equipment: 0, Materials: 0, Subs: 0, Other: 0 };
                    const lemsoRev = { Labor: 0, Equipment: 0, Materials: 0, Subs: 0, Other: 0 };
                    rowList.forEach((r, i) => {
                        const ct = (r.Cost_Type || '').toString();
                        const val = pNum(r.Value_Amount || 0);
                        const hrs = parseFloat(r.Hours) || 0;
                        const rev = billable.amounts[i] || 0;
                        if (ct >= '51000' && ct <= '51999') { lemso.Labor += val; lemsoHrs.Labor += hrs; lemsoRev.Labor += rev; }
                        else if (ct >= '52000' && ct <= '52999') { lemso.Equipment += val; lemsoHrs.Equipment += hrs; lemsoRev.Equipment += rev; }
                        else if (ct >= '53000' && ct <= '53999') { lemso.Materials += val; lemsoRev.Materials += rev; }
                        else if (ct >= '54000' && ct <= '54999') { lemso.Subs += val; lemsoRev.Subs += rev; }
                        else if (ct >= '55000' && ct <= '59999') { lemso.Other += val; lemsoRev.Other += rev; }
                    });
                    const grandTotal = Object.values(lemso).reduce((s, v) => s + v, 0);
                    const revTotal = Object.values(lemsoRev).reduce((s, v) => s + v, 0);
                    const entries = [['Labor', lemso.Labor, lemsoHrs.Labor, lemsoRev.Labor], ['Equipment', lemso.Equipment, lemsoHrs.Equipment, lemsoRev.Equipment], ['Materials', lemso.Materials, 0, lemsoRev.Materials], ['Subs', lemso.Subs, 0, lemsoRev.Subs], ['Other', lemso.Other, 0, lemsoRev.Other]];
                    return (
                        <View style={{ marginTop: 20, borderWidth: 1, borderColor: '#e2e8f0', backgroundColor: '#fff' }}>
                            <Pressable onPress={() => setExpandedSections(prev => ({ ...prev, [lemsoKey]: !prev[lemsoKey] }))}>
                                <Text style={{ fontSize: 15, fontWeight: '700', padding: 12, color: '#0F2A4A', backgroundColor: '#f8fafc', borderBottomWidth: 2, borderBottomColor: '#2563eb' }}>{expandedSections[lemsoKey] ? '▼' : '▶'} LEMSO Totals</Text>
                            </Pressable>
                            {expandedSections[lemsoKey] && <><View style={{ flexDirection: 'row', backgroundColor: '#f8fafc', borderBottomWidth: 2, borderBottomColor: '#2563eb' }}>
                                <Text style={{ width: 180, fontSize: 12, fontWeight: '600', padding: 10, color: '#1e293b' }}>Category</Text>
                                <Text style={{ width: 140, fontSize: 12, fontWeight: '600', padding: 10, textAlign: 'right', color: '#1e293b' }}>Amount</Text>
                                {billable.has && <Text style={{ width: 140, fontSize: 12, fontWeight: '600', padding: 10, textAlign: 'right', color: '#1e293b' }}>Billable Cost</Text>}
                                <Text style={{ width: 100, fontSize: 12, fontWeight: '600', padding: 10, textAlign: 'right', color: '#1e293b' }}>Hours</Text>
                            </View>
                            {entries.map(([cat, val, hrs, rev], i) => (
                                <View key={cat} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', backgroundColor: i % 2 === 0 ? '#fff' : '#F6F8FB' }}>
                                    <Text style={{ width: 180, fontSize: 13, padding: 10, color: '#0E1B2B' }}>{cat}</Text>
                                    <Text style={{ width: 140, fontSize: 13, padding: 10, textAlign: 'right', color: '#0E1B2B' }}>{fNum(val, 1)}</Text>
                                    {billable.has && <Text style={{ width: 140, fontSize: 13, padding: 10, textAlign: 'right', color: '#0E1B2B' }}>{fNum(rev, 1)}</Text>}
                                    <Text style={{ width: 100, fontSize: 13, padding: 10, textAlign: 'right', color: '#0E1B2B' }}>{hrs > 0 ? fNum(hrs) : '-'}</Text>
                                </View>
                            ))}
                            <View style={{ flexDirection: 'row', borderTopWidth: 2, borderTopColor: '#2563eb', backgroundColor: '#eff6ff' }}>
                                <Text style={{ width: 180, fontSize: 13, fontWeight: '700', padding: 10, color: '#0F2A4A' }}>Grand Total</Text>
                                <Text style={{ width: 140, fontSize: 13, fontWeight: '700', padding: 10, textAlign: 'right', color: '#0F2A4A' }}>{fNum(grandTotal, 1)}</Text>
                                {billable.has && <Text style={{ width: 140, fontSize: 13, fontWeight: '700', padding: 10, textAlign: 'right', color: '#0F2A4A' }}>{fNum(revTotal, 1)}</Text>}
                                <Text style={{ width: 100, fontSize: 13, padding: 10, textAlign: 'right', color: '#0F2A4A' }}></Text>
                            </View></>}
                        </View>
                    );
                })()}
                </>)}
            </View>
    );
};

export default DayZeroBudget;


